import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Box, Button, Center, Flex } from '@chakra-ui/react';
import { EOperatorType, EventEmitter, hasValue, useT } from '@progress-fe/core';
import { v4 as uuidv4 } from 'uuid';
import { TreeListDialog } from '../../dialogs';
import { useJsFieldFormData } from '../../../hooks';
import { JsFieldError, JsFieldInspector } from '../../jsFormBase';
import { JsFormConfigContext } from '../../../contexts';
import { OptimizationParam } from './components';
/**
 * Component renders "/schemas/jsf-optimization-param" field of JsonSchema.
 * @param props : field props come from JsonSchema.
 * Value of props.formData is an instance of TOptimizationParamFormData.
 */
const OptimizationParamJsFieldFC = (props) => {
    const [isParamDialog, setIsParamDialog] = useState(null);
    const [isValueDialog, setIsValueDialog] = useState(null);
    const { trees = [], isInspector } = useContext(JsFormConfigContext);
    const { t } = useT();
    const formData = useMemo(() => (hasValue(props.formData) ? props.formData : null), [props.formData]);
    const { fieldConfig, fieldValues, onChangeFieldValues, onChangeFieldConfig } = useJsFieldFormData({
        config: formData?.fieldConfig ?? null,
        values: formData?.fieldValues ?? null,
        onChange: props.onChange,
        watchPropsChanges: true
    });
    const handleAddParam = useCallback((entityUuid, entityName, entityType, data) => {
        if (fieldValues) {
            onChangeFieldValues({
                ...fieldValues,
                params: [
                    ...fieldValues.params,
                    {
                        uuid: uuidv4(),
                        entityUuid: entityUuid,
                        entityName: entityName,
                        entityType: entityType,
                        paramName: data.name ?? '',
                        paramDefaultName: data.name ?? '',
                        paramJsonPath: data.path ?? '',
                        paramUnits: data.units ?? null,
                        goal: fieldConfig?.isGoal ? 'min' : undefined,
                        condition: fieldConfig?.isCondition ? EOperatorType.GreaterThan : undefined,
                        value: null
                    }
                ]
            });
        }
    }, [fieldConfig?.isCondition, fieldConfig?.isGoal, fieldValues, onChangeFieldValues]);
    const handleChangeParam = useCallback((uuid, entityUuid, entityName, entityType, data) => {
        if (fieldValues) {
            onChangeFieldValues({
                ...fieldValues,
                params: fieldValues.params.map((param) => {
                    return param.uuid === uuid
                        ? {
                            uuid: param.uuid,
                            entityUuid: entityUuid,
                            entityName: entityName,
                            entityType: entityType,
                            paramName: data.name ?? '',
                            paramDefaultName: data.name ?? '',
                            paramJsonPath: data.path ?? '',
                            paramUnits: data.units ?? null,
                            goal: param.goal,
                            condition: param.condition,
                            value: null,
                            currentValue: hasValue(data.value) ? data.value.toString() : null
                        }
                        : param;
                })
            });
        }
    }, [fieldValues, onChangeFieldValues]);
    const handleChangeParamName = useCallback((uuid, name) => {
        if (fieldValues) {
            onChangeFieldValues({
                ...fieldValues,
                params: fieldValues.params.map((param) => ({
                    ...param,
                    paramName: param.uuid === uuid ? name : param.paramName
                }))
            });
        }
    }, [fieldValues, onChangeFieldValues]);
    const handleChangeGoal = useCallback((uuid, goal) => {
        if (fieldValues) {
            onChangeFieldValues({
                ...fieldValues,
                params: fieldValues.params.map((param) => ({
                    ...param,
                    goal: param.uuid === uuid ? goal : param.goal
                }))
            });
        }
    }, [fieldValues, onChangeFieldValues]);
    const handleChangeCondition = useCallback((uuid, condition) => {
        if (fieldValues) {
            onChangeFieldValues({
                ...fieldValues,
                params: fieldValues.params.map((param) => ({
                    ...param,
                    condition: param.uuid === uuid ? condition : param.condition
                }))
            });
        }
    }, [fieldValues, onChangeFieldValues]);
    const handleChangeValue = useCallback((uuid, value) => {
        if (fieldValues) {
            onChangeFieldValues({
                ...fieldValues,
                params: fieldValues.params.map((param) => ({
                    ...param,
                    value: param.uuid === uuid ? value : param.value
                }))
            });
        }
    }, [fieldValues, onChangeFieldValues]);
    const handleChangeValueJsonPath = useCallback((uuid, entityUuid, data) => {
        if (fieldValues) {
            const found = fieldValues.params?.find((ip) => ip.uuid === uuid);
            const newValue = found?.valueJsonPath !== data.path ? data.value : found?.value;
            const newValueMapped = hasValue(newValue) ? newValue.toString() : null;
            const newValueJsonPath = found?.valueJsonPath !== data.path ? data.path : null;
            const newValueEntityUuid = found?.valueEntityUuid !== data.path ? entityUuid : null;
            onChangeFieldValues({
                ...fieldValues,
                params: fieldValues.params.map((param) => ({
                    ...param,
                    value: param.uuid === uuid ? newValueMapped : param.value,
                    valueJsonPath: param.uuid === uuid ? newValueJsonPath : param.valueJsonPath,
                    valueEntityUuid: param.uuid === uuid ? newValueEntityUuid : param.valueEntityUuid
                }))
            });
        }
    }, [fieldValues, onChangeFieldValues]);
    const handleDeleteParam = useCallback((uuid) => {
        if (fieldValues) {
            onChangeFieldValues({
                ...fieldValues,
                params: fieldValues.params.filter((p) => p.uuid !== uuid)
            });
        }
    }, [fieldValues, onChangeFieldValues]);
    useEffect(() => {
        onChangeFieldConfig(formData?.fieldConfig);
    }, [formData?.fieldConfig, onChangeFieldConfig]);
    const hasParams = (fieldValues?.params ?? []).length > 0;
    if (!fieldConfig || !fieldValues) {
        return _jsx(JsFieldError, { propName: props.name });
    }
    return (_jsx(JsFieldInspector, { isActive: isInspector, fieldCode: props.name, fieldType: props.uiSchema?.['ui:field']?.toString() ?? '', valueType: ['object'], children: _jsxs(Box, { "data-testid": "OptimizationParamJsField-test", "data-name": props.name, children: [_jsxs(Flex, { p: '0 0 2px 0', flexDirection: 'column', gap: '8px', children: [hasParams ? (_jsx(Flex, { gap: '4px', flexDirection: 'column', children: fieldValues.params.map((param) => (_jsx(OptimizationParam, { param: param, isGoal: fieldConfig.isGoal, isCondition: fieldConfig.isCondition, isSchemaReadonly: props.schema.readOnly, valueLabel: fieldConfig.valueLabel, onChangeName: (v) => handleChangeParamName(param.uuid, v), onChangeGoal: (v) => handleChangeGoal(param.uuid, v), onChangeCondition: (v) => handleChangeCondition(param.uuid, v), onChangeValue: (v) => handleChangeValue(param.uuid, v), onDeleteParam: () => handleDeleteParam(param.uuid), onParamClick: () => setIsParamDialog({ uuid: param.uuid }), onGoToClick: () => EventEmitter.emit('SelectItem', param.entityUuid), onLinkClick: () => setIsValueDialog({ uuid: param.uuid }) }, param.uuid))) })) : (_jsx(Center, { h: '32px', color: 'bodyText', border: '1px solid', borderColor: 'border', borderRadius: '4px', children: _jsx(Box, { opacity: "0.6", children: t('common.noParams') }) })), !props.schema.readOnly && fieldValues.params.length < fieldConfig.maxAmount && (_jsx(Button, { size: 'xs', variant: 'ghost', onClick: () => setIsParamDialog({ uuid: null }), children: t('actions.add') }))] }), isParamDialog && !hasValue(isParamDialog.uuid) && (_jsx(TreeListDialog, { trees: trees, isClickOnActiveDisabled: true, title: t('common.chooseProp'), onClose: () => setIsParamDialog(null), onClickTreeItem: (treeId, treeName, treeType, data) => {
                        handleAddParam(treeId, treeName, treeType, data);
                        setIsParamDialog(null);
                    } })), isParamDialog && hasValue(isParamDialog.uuid) && (_jsx(TreeListDialog, { trees: trees, isClickOnActiveDisabled: true, title: t('common.chooseProp'), onClose: () => setIsParamDialog(null), activeItems: fieldValues.params
                        ?.filter((ip) => ip.uuid === isParamDialog.uuid)
                        .map((ip) => ({
                        uuid: '',
                        columnName: '',
                        treeId: ip.entityUuid ?? '',
                        jsonPath: ip.paramJsonPath ?? ''
                    })), onClickTreeItem: (treeId, treeName, treeType, data) => {
                        if (isParamDialog.uuid && data.path) {
                            handleChangeParam(isParamDialog.uuid, treeId, treeName, treeType, data);
                            setIsParamDialog(null);
                        }
                    } })), hasValue(isValueDialog?.uuid) && (_jsx(TreeListDialog, { trees: trees, title: t('common.chooseProp'), onClose: () => setIsValueDialog(null), activeItems: fieldValues.params
                        ?.filter((ip) => ip.uuid === isValueDialog.uuid && hasValue(ip.valueJsonPath))
                        .map((ip) => ({
                        uuid: ip.uuid,
                        treeId: ip.valueEntityUuid ?? '',
                        jsonPath: ip.valueJsonPath ?? ''
                    })), onClickTreeItem: (treeId, treeName, treeType, data) => {
                        if (hasValue(isValueDialog.uuid)) {
                            handleChangeValueJsonPath(isValueDialog.uuid, treeId, data);
                            setIsValueDialog(null);
                        }
                    } }))] }) }));
};
export const OptimizationParamJsField = OptimizationParamJsFieldFC;
//# sourceMappingURL=OptimizationParamJsField.js.map